import { computed, reactive, ref, type ComputedRef } from 'vue'
import type { Resource } from '.'
import { foldResources } from '.'
import { match } from 'ts-pattern'

// ------- Cursor-based ------- //

const extractCursor = (url?: string | null) => (
  url && new URLSearchParams(url.split('?')[1] || '').get('cursor')
) ?? undefined

/**
 * Helpers for Django cursor-paginated responses
 *
 * @example
 * ```vue-html
 *   <Button :disabled="!previous" @click="cursor = cursorPagination.previous(artistsResource)" />
 *   <Button :disabled="!next" @click="cursor = cursorPagination.next(artistsResource)" />
 * ```
 */
export const cursorPagination = {
  next: (resource: { data?: { next?: string | null, previous?: string | null } }) => extractCursor(resource.data?.next),
  previous: (resource: { data?: { next?: string | null, previous?: string | null } }) => extractCursor(resource.data?.previous),

  /**
   * Resolves all pages for cursor-paginated endpoints using co-induction - feeding the parsed cursor back into the parameters
   *
   * @param get_xs
   * @param params
   * @param options
   *
   * @see `foldResources` for how multiple results are reduced.
   */
  all: <
    P extends { cursor?: string },
    T,
    O
  >(
    get_xs: (params: P, options?: O) => ComputedRef<Resource<{ next?: string | null, results: T[] }>>,
    params: Omit<P, 'cursor'>,
    options?: O
  ) => computed(() => {
    const resources: Resource<{ next?: string | null, results: T[] }>[] = []

    let currentCursor: string | undefined

    while (true) {
      // The cast is required here to re-inject the extracted cursor into the strict parameters
      const { value } = get_xs({ ...params, cursor: currentCursor } as unknown as P, options)
      resources.push(value)

      if (value.status !== 'success' || !value.data.next) {
        break
      }

      currentCursor = extractCursor(value.data.next) || undefined
    }

    return foldResources<T>(
      resources.map(r => match(r)
        .with({ status: 'success' }, state => ({
          ...state,
          data: state.data.results
        }))
        .otherwise(state => ({
          ...state,
          data: state.data?.results ?? []
        }))
      )
    )
  })
}

export const usePaginatedResource = <
  P extends Record<string, any>,
  T
>(
  get_xs: (params: P & { cursor?: string }) => ComputedRef<Resource<{ next?: string | null, results: T[] }>>,
  initialParams: P
) => {
  const params = reactive(initialParams)
  const activeCursor = ref<string | undefined>(undefined)
  const cursorOwner = ref<string>(JSON.stringify(initialParams))

  const currentHash = computed(() => JSON.stringify(params))

  const cursor = computed({
    get: () => currentHash.value === cursorOwner.value ? activeCursor.value : undefined,
    set: (newCursor) => {
      activeCursor.value = newCursor
      cursorOwner.value = currentHash.value
    }
  })

  const resource = computed(() =>
    get_xs({ ...params, cursor: cursor.value } as P & { cursor?: string }).value
  )

  return computed(() => ({
    resource: resource.value,
    params,
    cursor: cursor.value,
    setCursor: (val: string | undefined) => { cursor.value = val }
  }))
}

// ------- Page-based ------- //

/**
 * Note: To add options, use this pattern: `getAll(params_ => useDataStore().get_albums(params, myOptions))`
 * @see `foldResources` for details
 *
 * @param get_xs
 * @param params
 * @param options
 *
 * @returns combined resources for all pages of a paginated request such as `get_albums`
 */
export const allPages = <
  P extends { page?: number },
  T,
  O
>(
  get_xs: (params: P, options?: O) => ComputedRef<Resource<{ next?: string | null, results: T[] }>>,
  params: Omit<P, 'page'>,
  options?: O
) => computed(() => {
  const resources: Resource<{ next?: string | null, results: T[] }>[] = []
  let page = 1

  while (true) {
    const { value } = get_xs({ ...params, page } as unknown as P, options)
    resources.push(value)

    if (value.status !== 'success' || !value.data.next) {
      break
    }

    page++
  }

  return foldResources<T>(
    resources.map(r => ({
      ...r,
      data: r.data?.results ?? []
    })
    )
  )
})
